import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Users, 
  TrendingUp, 
  Calendar, 
  Download, 
  Search,
  RefreshCw,
  Mail,
  Building,
  Phone,
  LogOut,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL as API } from '../config/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState({
    total_signups: 0,
    today_signups: 0,
    week_signups: 0,
    month_signups: 0
  });
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Filter entries based on search term
    const filtered = waitlistEntries.filter(entry => 
      entry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (entry.company && entry.company.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredEntries(filtered);
  }, [searchTerm, waitlistEntries]);


const fetchData = async () => {
  setLoading(true);

  try {
    const [statsResponse, entriesResponse] = await Promise.all([
      axios.get(`${API}/waitlist/stats`, {
        withCredentials: true
      }),
      axios.get(`${API}/waitlist/?limit=1000`, {
        withCredentials: true
      })
    ]);

    setStats(statsResponse.data.stats);
    setWaitlistEntries(entriesResponse.data.data);
    setFilteredEntries(entriesResponse.data.data);

    toast.success("Data loaded successfully");

  } catch (error) {
    console.error("Error fetching data:", error);
    toast.error(
      error.response?.data?.detail || "Failed to load data"
    );
  } finally {
    setLoading(false);
  }
};


  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/admin/login');
    } catch (error) {
      toast.error(
        error.response?.data?.detail || error.message || 'Logout failed'
      );
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };


  const handleDeleteConfirm = async () => {
    if (!entryToDelete || isDeleting) return;

    setIsDeleting(true);

    try {
      const tokenResponse = await axios.get(
        `${API}/admin-auth/csrf-token`,
        { withCredentials: true }
      );

      const response = await axios.delete(
        `${API}/waitlist/${entryToDelete.id}`,
        {
          withCredentials: true,
          headers: {
            'X-CSRF-Token': tokenResponse.data.csrfToken
          }
        }
      );

      if (response.data.success) {
        toast.success('Entry deleted successfully');
        await fetchData();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Failed to delete entry'
      );
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Company',  'Phone', 'Date'];
    const csvData = filteredEntries.map(entry => [
      entry.name,
      entry.email,
      entry.company || '',
      entry.phone  || '',
      new Date(entry.created_at).toLocaleString()
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `drivault_waitlist_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('CSV exported successfully');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-[#40E095]" size={48} />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
              <p className="text-gray-600">Manage your Drivault waitlist</p>
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={() => window.location.href = '/'}
                variant="outline"
                className="border-2 border-gray-200"
              >
                Back to Home
              </Button>
              <Button
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-2 border-gray-100 hover:border-[#40E095]/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Signups</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_signups}</p>
                </div>
                <div className="bg-[#40E095]/10 p-3 rounded-lg">
                  <Users className="text-[#40E095]" size={28} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-100 hover:border-[#40E095]/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Today</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.today_signups}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Calendar className="text-blue-600" size={28} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-100 hover:border-[#40E095]/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">This Week</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.week_signups}</p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <TrendingUp className="text-purple-600" size={28} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-100 hover:border-[#40E095]/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">This Month</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.month_signups}</p>
                </div>
                <div className="bg-orange-100 p-3 rounded-lg">
                  <Calendar className="text-orange-600" size={28} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Waitlist Entries */}
        <Card className="border-2 border-gray-100">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div>
                <CardTitle className="text-2xl">Waitlist Entries</CardTitle>
                <CardDescription>All signups from your landing page</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    type="text"
                    placeholder="Search by name, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-2 border-gray-200 focus:border-[#40E095]"
                  />
                </div>
                <Button
                  onClick={fetchData}
                  variant="outline"
                  className="border-2 border-gray-200"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Refresh
                </Button>
                <Button
                  onClick={exportToCSV}
                  className="bg-[#40E095] hover:bg-[#36C981] text-white"
                >
                  <Download size={16} className="mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredEntries.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="mx-auto mb-4 text-gray-400" size={48} />
                  <p className="text-gray-600">No entries found</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredEntries.map((entry) => (
                    <Card key={entry.id} className="border border-gray-200 hover:border-[#40E095]/50 transition-all">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                          <div className="space-y-3 flex-1">
                            <div className="flex items-center space-x-3">
                              <div className="bg-[#40E095]/10 p-2 rounded-lg">
                                <Users className="text-[#40E095]" size={20} />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{entry.name}</h3>
                                <Badge className="bg-[#40E095]/10 text-[#40E095] hover:bg-[#40E095]/20">
                                  {formatDate(entry.created_at)}
                                </Badge>
                              </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-3 ml-11">
                              <div className="flex items-center space-x-2 text-gray-600">
                                <Mail size={16} />
                                <a href={`mailto:${entry.email}`} className="hover:text-[#40E095] transition-colors">
                                  {entry.email}
                                </a>
                              </div>
                              {entry.company && (
                                <div className="flex items-center space-x-2 text-gray-600">
                                  <Building size={16} />
                                  <span>{entry.company}</span>
                                </div>
                              )}
                              {entry.phone && (
                                <div className="flex items-center space-x-2 text-gray-600">
                                  <Phone size={16} />
                                  <a href={`tel:${entry.phone}`} className="hover:text-[#40E095] transition-colors">
                                    {entry.phone}
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Delete Button */}
                          <div className="flex justify-end ml-4">
                            <Button
                              onClick={() => handleDeleteClick(entry)}
                              variant="outline"
                              size="sm"
                              className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                            >
                              <Trash2 size={16} className="mr-2" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the waitlist entry for{' '}
              <strong>{entryToDelete?.name}</strong> ({entryToDelete?.email}).
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
